/* eslint-disable max-lines-per-function */
import { Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { overquotaCommunicationPayPlacement } from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { renderNewComfirmationDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog.helpers';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { QUOTA_PAGE_BASE_URL } from 'reactApp/constants/magicIdentificators';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { useBuyClickHandlers } from 'reactApp/hooks/useBuyClickHandlers';
import { requestDataRestore, setClearedUserProcessed, setProfileUblockUser } from 'reactApp/modules/profile/profile.module';
import { historyPush } from 'reactApp/modules/router/router.module';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { Button } from 'reactApp/ui/Button/Button';
import {
    OverquotaBannerCtrl,
    sendDwhOverquota,
    sendRecoveryFlowDwh,
    sendTechOverqRadar,
} from 'reactApp/ui/OverquotaModal/OverquotaModal.helpers';
import type { PlaceType } from 'reactApp/ui/OverquotaModal/OverquotaModal.types';
import styles from 'reactApp/ui/OverquotaPopup/newSplash/OverquotaSplash.css';
import { getModalName } from 'reactApp/ui/OverquotaPopup/newSplash/OverquotaSplash.helpers';
import type { ModeType } from 'reactApp/ui/OverquotaPopup/newSplash/OverquotaSplash.types';

export const Footer: React.FC<{
    mode?: ModeType;
    onClose(): void;
    storage: EStorageType;
    place?: PlaceType;
    isMobile: boolean;
}> = memo(({ mode, storage, onClose, place, isMobile }) => {
    const dispatch = useDispatch();
    const email = useSelector(UserSelectors.getEmail);
    const name = getModalName(mode);

    const paySourceName = mode === 'full_blocked' ? 'mnz_overquota_splash_94day' : 'mnz_overquota_splash_31day';
    const { showPaywall } = useBuyClickHandlers({ paySource: paySourceName });

    const onGoToCloudHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, place, control: OverquotaBannerCtrl.go_cloud });
        dispatch(setClearedUserProcessed({ forceLA: true }));
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'gotocld' });
        onClose?.();
    }, [onClose, place]);

    const onUnblockHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, place, control: OverquotaBannerCtrl.unblocked, name });
        dispatch(setProfileUblockUser());
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'unblock' });
    }, [place]);

    const onWhyWhyWhy = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, place, control: OverquotaBannerCtrl.whyWhyWhy });
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'whywhy' });
    }, [place]);

    const onWhyNew = useCallback(() => {
        onWhyWhyWhy();
        open(`https://help.mail.ru/cloud_web/faq/?${PaymentUTM.overquotaSplashNewCleared}#overquota`, '_blank');
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'whynew' });
    }, [onWhyWhyWhy]);

    const onRestoreHandle = useCallback(() => {
        sendRecoveryFlowDwh({ action: 'click_buy', storage });

        dispatch(requestDataRestore());
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'restore' });
    }, []);

    const onAddSpaceHandler = useCallback(() => {
        if (mode === 'full_blocked') {
            sendDwhOverquota({
                action: 'click',
                storage,
                place: 'onboarding',
                control: OverquotaBannerCtrl.add_space,
                source: 'file',
                name,
            });
        } else {
            sendDwhOverquota({ action: 'click', storage, place, control: OverquotaBannerCtrl.add_space, name });
        }

        if (overquotaCommunicationPayPlacement === 'paywall') {
            showPaywall();
            return;
        }

        dispatch(historyPush({ id: QUOTA_PAGE_BASE_URL }));
    }, [onClose, place, mode]);

    const onPurgeHandler = useCallback(() => {
        sendRecoveryFlowDwh({ action: 'click_clear', storage });

        renderNewComfirmationDialog({
            renderHeader: () => 'Очистить Облако?',
            renderContent: () => <Text className={styles.dialogText}>После этого восстановить файлы не получится</Text>,
            buttons: [EButtonType.action, EButtonType.cancel],
            actionButtonText: 'Очистить навсегда',
            closeOnDimmerClick: true,
            isNegative: true,
            onAction: () => {
                sendRecoveryFlowDwh({ control: 'overquota_recovery_popup', action: 'click_clear', storage });
                dispatch(setClearedUserProcessed({ forceLA: true }));
            },
            dataQAId: 'confirm-cloud-purge',
        });
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'purge' });
    }, []);

    const onGoToClearHandler = useCallback(() => {
        if (mode === 'full_blocked') {
            sendDwhOverquota({
                action: 'click',
                storage,
                place: 'onboarding',
                control: OverquotaBannerCtrl.clear,
                source: 'file',
                name,
            });
        }

        dispatch(historyPush({ id: QUOTA_PAGE_BASE_URL, hash: 'cleaner' }));
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'gotoclear' });
    }, [onClose]);

    const handleChangeAccountClick = useCallback(() => {
        const path = storage === EStorageType.home ? '/home' : window.location.pathname;

        const successPage = `${window.location.origin}${path}`;

        window.__PH.authForm.show({
            formType: 'relogin',
            successPage,
            email,
        });
        sendTechOverqRadar({ state: mode, action: 'click', bttnName: 'accchn' });
    }, [email, storage]);

    if (mode === 'cleared') {
        return (
            <div className={classNames(styles.buttons, styles.buttonsNewClear)}>
                <Button className={styles.button} data-name="freeze-renewal" theme="vk" onClick={onGoToCloudHandler} primary>
                    Перейти в Облако
                </Button>
                <Button className={styles.button} onClick={onWhyNew} theme="octavius" data-name="secondary">
                    Узнать -- почему?
                </Button>
                {Boolean(isMobile) && (
                    <Button
                        className={classNames(styles.button, styles.button_tetriary)}
                        onClick={handleChangeAccountClick}
                        theme="octavius"
                        data-name="secondary"
                    >
                        Войти в другой аккаунт
                    </Button>
                )}
            </div>
        );
    }

    if (mode === 'after_clear') {
        return (
            <div className={styles.buttons}>
                <Button className={styles.button} data-name="freeze-renewal-after-clear" theme="vk" onClick={onRestoreHandle} primary>
                    Снять ограничение
                </Button>
                <Button className={styles.button} onClick={onPurgeHandler} theme="octavius" data-name="secondary">
                    Очистить Облако
                </Button>
            </div>
        );
    }

    if (mode === 'full_blocked') {
        return (
            <div className={styles.buttons}>
                <Button className={styles.button} data-name="freeze-renewal" theme="octavius" onClick={onAddSpaceHandler} primary>
                    Вернуть доступ с подпиской
                </Button>
                <Button className={styles.button} onClick={onGoToClearHandler} theme="octavius" data-name="secondary">
                    Удалить лишнее
                </Button>
                {Boolean(isMobile) && (
                    <Button
                        className={classNames(styles.button, styles.button_tetriary)}
                        onClick={handleChangeAccountClick}
                        theme="octavius"
                        data-name="secondary"
                    >
                        Войти в другой аккаунт
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div className={styles.buttons}>
            <Button className={styles.button} data-name="freeze-renewal" theme="octavius" onClick={onAddSpaceHandler} primary>
                Увеличить место
            </Button>
            <Button className={styles.button} onClick={onUnblockHandler} theme="octavius" data-name="secondary">
                Снять ограничение временно
            </Button>
            {Boolean(isMobile) && (
                <Button
                    className={classNames(styles.button, styles.button_tetriary)}
                    onClick={handleChangeAccountClick}
                    theme="octavius"
                    data-name="secondary"
                >
                    Войти в другой аккаунт
                </Button>
            )}
        </div>
    );
});

Footer.displayName = 'Footer';
